import {showNotification} from 'platform/components';
import {HStack, Link, Show, Text, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useAutoplacPlAuthenticateMutation,
  useGetAutoplacPlExtraLinkQuery,
  useGetAutoplacPlPlatformQuery,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useBranchId} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {
  PlatformAuthenticationFormUrlExport,
  PlatformAuthenticationFormUrlExportProps,
} from './PlatformAuthenticationFormUrlExport';
import {PlatformPhotoSettings} from './PlatformPhotoSettings';
import {PlatformSettingsPromptBridge} from './PlatformSettingsPromptBridge';

export function PlatformSettingsAutoplacPl() {
  return (
    <>
      <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_AUTOPLAC_PL_PROMPT_BRIDGE}>
        <VStack spacing={4}>
          <PlatformSettingsPromptBridge code={AdvertisingPlatformCodeEnum.AUTOPLAC_PL} />
          <PlatformSettingsAutoplacPlFeedLink />
        </VStack>
      </Show>
      <Show whenFeatureDisabled={featureFlags.ADVERTISEMENT_AUTOPLAC_PL_PROMPT_BRIDGE}>
        <PlatformSettingsAutoplacPlLegacy />
      </Show>
      <PlatformPhotoSettings platformCode={AdvertisingPlatformCodeEnum.AUTOPLAC_PL} />
    </>
  );
}

function PlatformSettingsAutoplacPlFeedLink() {
  const branchId = useBranchId();
  const {data} = useGetAutoplacPlExtraLinkQuery({
    branchId,
    publishAutoplacPlViaAdvertisingBridge: true,
  });
  const link = data?.link;

  return (
    <Show when={isNotNilOrEmpty(link) ? link : null}>
      {(value) => (
        <HStack spacing={1} align="center">
          <Text size="xSmall">{i18n.t('general.labels.URL')}</Text>
          <Link
            size="small"
            data-testid={testIds.settings.advertisingPlatforms('externalLink-urlExport')}
            href={value}
            title={i18n.t('page.advertisement.labels.extraLinkText')}
            target="_blank"
          />
        </HStack>
      )}
    </Show>
  );
}

function PlatformSettingsAutoplacPlLegacy() {
  const branchId = useBranchId();
  const params = {branchId};
  const {data, isFetching, isError} = useGetAutoplacPlPlatformQuery({params});
  const {data: extraLinkData} = useGetAutoplacPlExtraLinkQuery(params);
  const [saveAuthentication] = useAutoplacPlAuthenticateMutation();

  const onSubmitCredentials: PlatformAuthenticationFormUrlExportProps['onSubmit'] = (
    values,
    _setErrors,
    reset
  ) =>
    saveAuthentication({body: values, params})
      .unwrap()
      .then(() => {
        showNotification.success(
          i18n.t('entity.advertisingPlatforms.notifications.credentialsSaved')
        );
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);

  return (
    <PlatformAuthenticationFormUrlExport
      values={data?.credentials}
      extraLink={extraLinkData?.link}
      isFetching={isFetching}
      isError={isError}
      onSubmit={onSubmitCredentials}
      data-testid={testIds.settings.advertisingPlatforms('platformAuthenticationFormUrlExport')}
    />
  );
}
